import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import { getHeroUrl } from '../../utils/cloudinaryHelpers';

/**
 * HeroCarousel — CMS-driven home hero with graceful fallback.
 *
 * Reads /api/carousel and renders:
 *   - 0 slides → returns the `fallback` prop (the caller's hardcoded
 *                 hero — e.g. "Culte du Dimanche"). Renders fallback
 *                 IMMEDIATELY on first paint when no cache is present,
 *                 so there's never a flash of empty box.
 *   - 1 slide  → static hero, no dots, no auto-rotate.
 *   - 2+ slides → auto-rotating carousel with fade transition + dot
 *                  indicators + swipe + pause-on-touch.
 *
 * Slide shape from /api/carousel:
 *     { id, imageUrl, title?, subtitle?, ctaUrl?, order, status }
 *
 * The component intentionally NEVER throws on fetch error — a network
 * blip falls through to the fallback hero rather than crashing the
 * home screen. The caller treats `loading || empty` identically and
 * renders the hardcoded fallback in the meantime.
 *
 * Cache strategy (updated June 2026 — bug: deleted banner persisted in
 * the app until the user manually reloaded):
 *   - 15 s TTL in module-scope memory + sessionStorage (was 5 min). On
 *     mount we still paint cached slides instantly to avoid the flash
 *     of empty box, but the cache invalidates fast enough that
 *     subsequent navigation never shows stale CMS content for more
 *     than ~15 s.
 *   - Refetch on `visibilitychange` when the document becomes visible.
 *     Catches the "user comes back to the tab / app" case which is the
 *     common path after an admin update.
 *   - Periodic refetch every 15 s WHILE the document is visible. Skipped
 *     when hidden so an idle background tab doesn't poll /api/carousel.
 *   - Same pattern applied to /api/banners/active in SeasonalBanner.
 *     (/api/home-banner has no FE consumer yet.)
 */

import { API_BASE as BACKEND_URL } from '../../utils/apiBase';
const CACHE_KEY = 'pcc-hero-carousel-v1';
const CACHE_TTL_MS = 15 * 1000;               // 15 s — fast CMS invalidation
const POLL_INTERVAL_MS = 15 * 1000;           // Re-fetch every 15 s while visible
const AUTO_ROTATE_MS = 5000;                  // 5 seconds per slide
const TOUCH_PAUSE_RESUME_MS = 4000;           // Resume rotation ~4s after touch ends
const SWIPE_THRESHOLD_PX = 40;

// Module-level cache — persists across mounts within a single SPA session
// without paying the sessionStorage cost on every read.
let _memCache = null; // { data: [], fetchedAt: number }

function readCache() {
  if (_memCache && (Date.now() - _memCache.fetchedAt) < CACHE_TTL_MS) {
    return _memCache.data;
  }
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.fetchedAt !== 'number') return null;
    if ((Date.now() - parsed.fetchedAt) >= CACHE_TTL_MS) return null;
    _memCache = parsed;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function writeCache(data) {
  _memCache = { data, fetchedAt: Date.now() };
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(_memCache));
  } catch (_) {
    // Out of quota — ignore.
  }
}

const HeroCarousel = ({ fallback = null }) => {
  const [slides, setSlides] = useState(() => readCache() || []);
  const [height, setHeight] = useState('standard'); // hauteur réglée via le CMS
  const [activeIdx, setActiveIdx] = useState(0);

  const intervalRef = useRef(null);
  const touchStartXRef = useRef(null);
  const touchActiveRef = useRef(false);
  const resumeTimerRef = useRef(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────
  const fetchSlides = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/carousel`, { credentials: 'include' });
      if (!resp.ok) return;
      const data = await resp.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      if (typeof data?.height === 'string') setHeight(data.height);
      // Filter to slides with an image — anything else can't render usefully.
      const usable = items.filter(s => s && s.imageUrl);
      setSlides(usable);
      writeCache(usable);
    } catch (_) {
      // Network blip — fall back to existing (cached) slides if any.
    }
  }, []);

  useEffect(() => {
    // Always re-fetch on mount — the cache only spares us instantly on
    // re-mounts within the 15 s window. A fresh tab open still hits the
    // API.
    fetchSlides();

    // Visibility refetch: when the user comes back to the tab / app, pull
    // fresh data. This is the most common path after an admin updates the
    // carousel in the CMS.
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchSlides();
    };
    document.addEventListener('visibilitychange', onVis);

    // 15 s polling, but ONLY when the document is visible. Mobile and
    // backgrounded desktop tabs don't burn /api/carousel calls.
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') fetchSlides();
    }, POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(pollId);
    };
  }, [fetchSlides]);


  // ─── Auto-rotation ──────────────────────────────────────────────────────
  const startAutoRotate = useCallback(() => {
    stopAutoRotate();
    if (slides.length < 2) return;
    intervalRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % slides.length);
    }, AUTO_ROTATE_MS);
  }, [slides.length]);

  const stopAutoRotate = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    startAutoRotate();
    return () => {
      stopAutoRotate();
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
    // Re-arm when the slide count changes (e.g. CMS adds a slide
    // during a long-lived session and the cache invalidates).
  }, [startAutoRotate]);

  // ─── Touch / swipe ──────────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    if (slides.length < 2) return;
    touchActiveRef.current = true;
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
    stopAutoRotate();
  };

  const handleTouchMove = () => {
    // We intentionally don't preview the swipe distance — the spec asks
    // for "manual swipe to navigate" and "pause on touch", both
    // satisfied by deferring the index change to touchend.
  };

  const handleTouchEnd = (e) => {
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (typeof startX === 'number' && e.changedTouches?.[0]) {
      const endX = e.changedTouches[0].clientX;
      const dx = endX - startX;
      if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) {
        setActiveIdx(i => {
          const next = dx < 0 ? i + 1 : i - 1;
          return ((next % slides.length) + slides.length) % slides.length;
        });
      }
    }
    // Resume auto-rotate after a short delay so the user isn't whisked
    // away mid-read.
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => startAutoRotate(), TOUCH_PAUSE_RESUME_MS);
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  // - Before first fetch completes AND with no cache: render fallback so
  //   the user never sees an empty box. The most likely outcome is "no
  //   carousel configured" → the hardcoded hero is correct.
  // - After fetch completes with 0 slides: render fallback.
  // - With 1+ slides: render the carousel below.
  if (slides.length === 0) {
    return fallback;
  }

  const isCarousel = slides.length > 1;

  return (
    <div
      // Mobile: aspect-[4/5] gives the banner enough vertical room to
      // surface squarer/portrait CMS uploads (e.g. "Baptême d'eau")
      // without cropping the top/bottom — was aspect-[16/9] which made
      // ~220 px on iPhone-width and ate the focal point.
      // Desktop (md+): stays widescreen at 16/9 so the home page
      // doesn't get an oversized banner above the fold.
      // max-h caps the worst case on tall mobile screens.
      // La hauteur est réglable via le CMS (compact / standard / grand).
      className={`relative w-full overflow-hidden ${
        height === 'compact'
          ? 'aspect-[16/10] md:aspect-[16/9] max-h-[420px]'
          : height === 'grand'
            ? 'aspect-[3/4] md:aspect-[16/9] max-h-[680px]'
            : 'aspect-[4/5] md:aspect-[16/9] max-h-[560px]'
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="hero-carousel"
    >
      {/* Stacked slides — only the active one is at full opacity. CSS
          transition handles the fade so we don't unmount images
          (which would re-trigger the network on the next rotation). */}
      {slides.map((slide, idx) => {
        const isActive = idx === activeIdx;
        const hasCta = !!(slide.ctaUrl || slide.ctaLabel);
        const onClickSlide = () => {
          if (!slide.ctaUrl) return;
          if (slide.ctaUrl.startsWith('http')) {
            window.open(slide.ctaUrl, '_blank', 'noopener,noreferrer');
          } else {
            window.location.assign(slide.ctaUrl);
          }
        };
        return (
          <div
            key={slide.id || idx}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              hasCta ? 'cursor-pointer' : ''
            }`}
            style={{
              opacity: isActive ? 1 : 0,
              pointerEvents: isActive ? 'auto' : 'none',
            }}
            onClick={hasCta ? onClickSlide : undefined}
            role={hasCta ? 'link' : undefined}
            tabIndex={hasCta && isActive ? 0 : -1}
            onKeyDown={hasCta ? (e) => { if (e.key === 'Enter') onClickSlide(); } : undefined}
            aria-hidden={!isActive}
            data-testid={`hero-slide-${idx}`}
          >
            <img
              src={getHeroUrl(slide.imageUrl)}
              alt={slide.title || ''}
              className="absolute inset-0 w-full h-full object-cover"
              // Explicit intrinsic size prevents cumulative layout
              // shift while the image loads — the aspect wrapper still
              // controls actual rendering. 2000×1600 matches the 4/5
              // mobile ratio at Cloudinary getHeroUrl(1600 max).
              width={2000}
              height={1600}
              fetchPriority={isActive ? 'high' : 'low'}
              decoding="async"
              loading={idx === 0 ? 'eager' : 'lazy'}
            />
            {/* Status-bar darkening at the top so the notch stays legible */}
            <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none pcc-status-bar-gradient" />
            {/* Bottom-up dark gradient for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            {/* Text + optional CTA */}
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
              <div className="max-w-4xl mx-auto text-center">
                {slide.title && (
                  <h2 className="text-3xl md:text-5xl font-bold mb-3 tracking-tight">
                    {slide.title}
                  </h2>
                )}
                {slide.subtitle && (
                  <p className="text-lg md:text-xl text-white/90 mb-4 tracking-wide">
                    {slide.subtitle}
                  </p>
                )}
                {slide.ctaLabel && (
                  <span
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white"
                    style={{ background: '#D7A072' }}
                  >
                    <MapPin size={14} />
                    {slide.ctaLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Dot indicators — only when there are 2+ slides */}
      {isCarousel && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5"
          data-testid="hero-carousel-dots"
        >
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setActiveIdx(idx);
                // User asked for a specific slide — pause briefly, then resume.
                stopAutoRotate();
                if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
                resumeTimerRef.current = setTimeout(() => startAutoRotate(), TOUCH_PAUSE_RESUME_MS);
              }}
              className="p-1.5 -m-1 transition-all"
              aria-label={`Aller au slide ${idx + 1}`}
              aria-current={idx === activeIdx ? 'true' : undefined}
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  idx === activeIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                }`}
                style={{ height: '6px' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
